import { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { X } from 'lucide-react';
import { meetingsAPI } from '../../lib/api';

// Change C (26 Oct 2026, Lex's later round): "Attended by (VSTL)" — a mix of users
// with a login (active, MEETINGS CREATE) and Admin-managed field visitors without one.
// "I attended" is just a convenience toggle for whether the logger is in this list.
export default function AttendeesPicker({ selfId, selfName, userIds, visitorIds, namesUntagged = [],
                                           onChangeUserIds, onChangeVisitorIds, onChangeNamesUntagged, allowTypedName = true }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [pickedUsers, setPickedUsers] = useState([]);
  const [typedName, setTypedName] = useState('');
  const debounceRef = useRef(null);

  useEffect(() => { meetingsAPI.listFieldVisitors().then((r) => setVisitors(r.data.field_visitors || [])).catch(() => {}); }, []);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await meetingsAPI.searchColleagues(q);
        setResults((res.data.colleagues || []).filter((c) => c.id !== selfId && !userIds.includes(c.id)));
      } catch { setResults([]); }
    }, 300);
  };

  const attended = userIds.includes(selfId);
  const toggleAttended = (checked) => {
    onChangeUserIds(checked ? [...userIds, selfId] : userIds.filter((id) => id !== selfId));
  };

  const addUser = (c) => {
    setPickedUsers((p) => [...p, c]);
    onChangeUserIds([...userIds, c.id]);
    setResults([]); setQuery('');
  };
  const removeUser = (id) => {
    onChangeUserIds(userIds.filter((u) => u !== id));
    setPickedUsers((p) => p.filter((c) => c.id !== id));
  };
  const toggleVisitor = (id) => {
    onChangeVisitorIds(visitorIds.includes(id) ? visitorIds.filter((v) => v !== id) : [...visitorIds, id]);
  };
  // Round 3 (31 Oct 2026, Visits & Meetings): a typed VSTL attendee name — no lookup,
  // counts under the literal typed text until tagged to a real person later.
  const addTypedName = () => {
    const n = typedName.trim();
    if (!n || namesUntagged.includes(n)) { setTypedName(''); return; }
    onChangeNamesUntagged([...namesUntagged, n]);
    setTypedName('');
  };
  const removeTypedName = (n) => onChangeNamesUntagged(namesUntagged.filter((x) => x !== n));

  const otherUserNames = userIds.filter((id) => id !== selfId).map((id) => {
    const p = pickedUsers.find((c) => c.id === id);
    return p ? p.name : id;
  });

  return (
    <div className="space-y-2" data-testid="attendees-picker">
      <Label>Attended by (VSTL)</Label>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="attended-self-checkbox-label">
        <Checkbox checked={attended} onCheckedChange={toggleAttended} data-testid="attended-self-checkbox" />
        I attended ({selfName || 'you'})
      </label>
      <div className="flex flex-wrap gap-1.5">
        {otherUserNames.map((name, i) => (
          <Badge key={i} variant="secondary" className="gap-1" data-testid={`attendee-user-badge-${i}`}>
            {name}
            <button type="button" onClick={() => removeUser(userIds.filter((id) => id !== selfId)[i])}><X className="h-3 w-3" /></button>
          </Badge>
        ))}
        {visitorIds.map((vid) => {
          const v = visitors.find((x) => x.id === vid);
          return (
            <Badge key={vid} variant="outline" className="gap-1" data-testid={`attendee-visitor-badge-${vid}`}>
              {v ? v.name : vid} (field visitor)
              <button type="button" onClick={() => toggleVisitor(vid)}><X className="h-3 w-3" /></button>
            </Badge>
          );
        })}
        {namesUntagged.map((n, i) => (
          <Badge key={`untagged-${i}`} variant="outline" className="gap-1 border-dashed" data-testid={`attendee-untagged-badge-${i}`}>
            {n} (typed, to tag)
            {allowTypedName && <button type="button" onClick={() => removeTypedName(n)}><X className="h-3 w-3" /></button>}
          </Badge>
        ))}
      </div>
      <Input placeholder="Add another colleague..." value={query} onChange={(e) => search(e.target.value)} data-testid="attendee-search-input" />
      {results.length > 0 && (
        <div className="border rounded-sm divide-y max-h-36 overflow-y-auto">
          {results.map((c) => (
            <button key={c.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/60"
              data-testid={`attendee-option-${c.id}`} onClick={() => addUser(c)}>{c.name}</button>
          ))}
        </div>
      )}
      {allowTypedName && (
        <div className="flex items-end gap-2">
          <Input className="flex-1" placeholder="Don't know who? Type a name instead (tag it later)..." value={typedName}
            onChange={(e) => setTypedName(e.target.value)} data-testid="attendee-typed-name-input" />
          <Button type="button" size="sm" variant="outline" onClick={addTypedName} data-testid="attendee-typed-name-add-btn">Add</Button>
        </div>
      )}
      {visitors.filter((v) => v.active).length > 0 && (
        <div>
          <Label className="text-xs text-muted-foreground">Field visitors</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {visitors.filter((v) => v.active).map((v) => (
              <label key={v.id} className="flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs cursor-pointer" data-testid={`field-visitor-option-${v.id}`}>
                <Checkbox checked={visitorIds.includes(v.id)} onCheckedChange={() => toggleVisitor(v.id)} />
                {v.name}{v.office ? ` (${v.office})` : ''}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
